import { useEffect, useState } from "react";
import type { Discount, Order, OrderEvent } from "../../api/types";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { discountLabel } from "../general/general";
import { reprintToast } from "../printing/print-notices";
import { printAgain } from "../printing/reprint";
import { actionsFor } from "./order-view";
import { Receipt } from "./Receipt";
import { receiptPaper } from "./ReceiptDialog";

interface Props {
  order: Order | null;
  onEdit: (order: Order) => void;
  onCancel: (order: Order) => void;
  /** Beheer › Bestellingen: only the receipt and its history; setting right happens in the list. */
  readOnly?: boolean;
}

/**
 * The right-hand side of the orders screen: the receipt, print and edit, the order's history and
 * cancelling. In Beheer the panel only shows the receipt and its history.
 */
export function OrderPanel({ order, onEdit, onCancel, readOnly = false }: Props) {
  const { api, status, general, toast, report, receiptQr } = useTill();
  const [history, setHistory] = useState<OrderEvent[]>([]);
  const [printing, setPrinting] = useState(false);
  const [askQr, setAskQr] = useState(false);
  const [askPickup, setAskPickup] = useState(false);

  const orderId = order?.id;
  const revision = order?.revision;
  useEffect(() => {
    if (orderId === undefined) return;
    const controller = new AbortController();
    api
      .request<OrderEvent[]>("GET", `/orders/${orderId}/history`, { retry: true, signal: controller.signal })
      .then(setHistory)
      .catch(() => undefined); // extra information; the receipt is shown anyway
    return () => {
      controller.abort();
    };
  }, [api, orderId, revision]);

  if (!order) {
    return (
      <aside className="bonpanel">
        <p className="empty">{t.orders.selectHint}</p>
      </aside>
    );
  }

  const actions = actionsFor(order);
  /**
   * "Klant haalt zelf af" (Furkan, 11/10/2026): a delivery not on the way and not paid becomes a takeaway
   * without the delivery cost, then paid as any takeaway.
   */
  const pickup =
    order.channel === "delivery" &&
    order.status === "open" &&
    order.source === "till" &&
    order.paidCents === 0 &&
    order.departedAt === null &&
    order.deliveredAt === null;
  const toTakeaway = async () => {
    setAskPickup(false);
    try {
      await api.request("POST", `/orders/${order.id}/to-takeaway`, { body: { expectedRevision: order.revision } });
      toast(t.orders.pickup.done(order.code));
    } catch (error) {
      report(error);
    }
  };

  /** "🖨 Afdrukken" (prototype): "Bon X3K afgedrukt op Kassaprinter toog"; without printer via the browser. */
  const print = async () => {
    setPrinting(true);
    try {
      const said = reprintToast(await printAgain(api, [order.id]), order.code);
      if (said === null) window.print();
      else toast(said);
    } catch (error) {
      report(error);
    } finally {
      setPrinting(false);
    }
  };

  /**
   * "Met QR afdrukken" (Furkan, 10/10/2026: "de klant zegt achteraf: ik wil met QR betalen"): the receipt
   * again, with Bancontact Pro QR for what is due (ADR 0029). Paid already: what was paid goes back first,
   * after a question, and the order waits for the QR.
   */
  const paid = order.status === "paid" && order.paidCents > 0;
  const withQr = !readOnly && receiptQr && actions.edit;
  const printQr = async (undoPayments: boolean) => {
    setPrinting(true);
    try {
      const answer = await api.request<Order>("POST", `/orders/${order.id}/receipt-qr`, {
        body: undoPayments ? { undoPayments: true } : {},
      });
      toast(t.checkout.receiptQr.printed(formatEuro(answer.totalCents - answer.paidCents)));
    } catch (error) {
      report(error);
    } finally {
      setPrinting(false);
    }
  };
  const methods = [
    ...new Set(order.payments.filter((item) => item.kind === "payment").map((item) => t.paymentMethods[item.method])),
  ].join(", ");

  return (
    <aside className="bonpanel" aria-label={`${t.orders.receipt} ${order.code}`}>
      <header className="bonpanel-head">
        <h3>
          {t.orders.receipt} <span className="code">{order.code}</span>
        </h3>
        <span className="spacer" />
        <button type="button" className="btn small" disabled={printing} onClick={() => void print()}>
          🖨 {t.orders.print}
        </button>
        {withQr && (
          <button
            type="button"
            className="btn small"
            title={t.orders.printQrTitle}
            disabled={printing}
            onClick={() => {
              if (paid) setAskQr(true);
              else void printQr(false);
            }}
          >
            🖨 {t.orders.printQr}
          </button>
        )}
        {!readOnly && actions.edit && (
          <button type="button" className="btn small" onClick={() => onEdit(order)}>
            {t.orders.edit}
          </button>
        )}
        {!readOnly && pickup && (
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setAskPickup(true);
            }}
          >
            {t.orders.pickup.button}
          </button>
        )}
      </header>

      <div className="bonwrap">
        <Receipt order={order} shop={status.shop} general={general} paper={receiptPaper(status.device.type)} />
      </div>

      {history.length > 0 && (
        <section className="grp">
          <h3>{t.orders.history}</h3>
          <ul className="history">
            {history.map((event, index) => (
              <li key={index}>
                <time>{formatTime(event.createdAt)}</time>
                <span>
                  {/* without a code the till itself did it: only the device is named */}
                  {[t.orders.events[event.type], event.staffName, event.deviceName].filter(Boolean).join(" · ")}
                  {describeDetails(event)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {askQr && (
        <ConfirmDialog
          message={t.orders.printQrPaid(order.code, formatEuro(order.paidCents), methods)}
          confirm={t.orders.printQrPaidConfirm}
          tone="primary"
          onConfirm={() => void printQr(true)}
          onClose={() => {
            setAskQr(false);
          }}
        />
      )}

      {askPickup && (
        <ConfirmDialog
          message={t.orders.pickup.confirm(
            order.code,
            order.deliveryCents > 0 ? formatEuro(order.deliveryCents) : null,
          )}
          confirm={t.orders.pickup.button}
          tone="primary"
          onConfirm={() => void toTakeaway()}
          onClose={() => {
            setAskPickup(false);
          }}
        />
      )}

      {!readOnly && actions.cancel && (
        <button type="button" className="btn small danger ghost" onClick={() => onCancel(order)}>
          {t.orders.cancel}
        </button>
      )}
    </aside>
  );
}

/** A short note per history entry: what was added or removed, which payment method changed. */
function describeDetails(event: OrderEvent): string {
  const details = event.details;
  if (event.type === "edited") {
    const added = (details.added as string[] | undefined) ?? [];
    const removed = (details.removed as string[] | undefined) ?? [];
    // "Korting" while editing: what the order has from then on
    const discount = details.discountAfter as Discount | null | undefined;
    return [
      ...added.map((item) => ` · + ${item}`),
      ...removed.map((item) => ` · − ${item}`),
      ...(discount === undefined
        ? []
        : [` · ${t.orders.editDiscount(discount === null ? null : discountLabel(discount))}`]),
    ].join("");
  }
  if (event.type === "payment_method_changed") {
    const from = t.paymentMethods[details.from as keyof typeof t.paymentMethods];
    const to = t.paymentMethods[details.to as keyof typeof t.paymentMethods];
    return ` · ${from} → ${to} (${formatEuro(details.amountCents as number)})`;
  }
  if (event.type === "cancelled" && typeof details.reason === "string") return ` · ${details.reason}`;
  if (event.type === "assigned") return ` · ${t.orders.assignedTo((details.courier as string | null) ?? null)}`;
  const text = (key: string) => (typeof details[key] === "string" ? details[key] : null);
  if (event.type === "courier_changed") return ` · ${text("previous") ?? "?"} → ${text("courier") ?? "?"}`;
  if (event.type === "unsettled")
    return [text("courier"), text("reason")].map((part) => (part ? ` · ${part}` : "")).join("");
  if (event.type === "problem" && text("reason")) return ` · ${text("reason") ?? ""}`;
  if (["departed", "delivered", "settled"].includes(event.type) && text("courier"))
    return ` · ${text("courier") ?? ""}`;
  if (event.type === "to_takeaway" && typeof details.deliveryCents === "number" && details.deliveryCents > 0)
    return ` · ${t.orders.deliveryOff(formatEuro(details.deliveryCents))}`;
  return "";
}
